/**
 * Command palette (⌘K / Ctrl+K) — instant access to every page, action,
 * and conversation. Linear/Vercel/Raycast pattern.
 *
 * Features:
 * - Fuzzy search across pages, conversations, and actions
 * - Keyboard-driven (arrow keys + Enter)
 * - Opens on ⌘K or Ctrl+K, closes on Escape
 * - Grouped results: Navigation, Conversations, Actions
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, MessageSquare, Plus, Search, Settings, Users, BookOpen, Cable, Zap } from 'lucide-react';
import { chatStore, useChatConversationList } from '@/lib/chatStore';
import { focusChatComposer } from '@/lib/composerFocus';
import { useIsMobile } from '@/lib/useIsMobile';
import { cn } from '@onestone/ui/utils';
import { PALETTE_MAX_CONVERSATIONS } from '@/lib/uiConstants';

interface CommandItem {
  id: string;
  label: string;
  group: 'navigation' | 'conversations' | 'actions';
  icon: typeof Search;
  action: () => void;
  keywords?: string;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const conversations = useChatConversationList();
  const isMobile = useIsMobile();
  // The chat is a page on phones and the side panel on desktop, where /chat
  // redirects to the default page (`ChatRoute`) — so desktop focuses the panel.
  const showChat = (): void => {
    if (isMobile) void navigate('/chat');
    else focusChatComposer();
  };

  // Build command items
  const items: CommandItem[] = [
    // Navigation
    {
      id: 'nav-chat',
      label: 'Chat',
      group: 'navigation',
      icon: MessageSquare,
      action: showChat,
      keywords: 'conversation talk',
    },
    {
      id: 'nav-routines',
      label: 'Routines',
      group: 'navigation',
      icon: Clock,
      action: () => navigate('/routines'),
      keywords: 'cron schedule',
    },
    {
      id: 'nav-calendar',
      label: 'Calendar',
      group: 'navigation',
      icon: Calendar,
      action: () => navigate('/calendar'),
      keywords: 'events dates',
    },
    { id: 'nav-contacts', label: 'Contacts', group: 'navigation', icon: Users, action: () => navigate('/contacts') },
    {
      id: 'nav-knowledge',
      label: 'Knowledge',
      group: 'navigation',
      icon: BookOpen,
      action: () => navigate('/knowledge'),
    },
    {
      id: 'nav-connections',
      label: 'Connections',
      group: 'navigation',
      icon: Cable,
      action: () => navigate('/connections'),
      keywords: 'providers api key claude subscription credentials integrations',
    },
    {
      id: 'nav-settings',
      label: 'Settings',
      group: 'navigation',
      icon: Settings,
      action: () => navigate('/settings'),
      keywords: 'model routines tools config',
    },

    // Actions
    {
      id: 'act-new-chat',
      label: 'New conversation',
      group: 'actions',
      icon: Plus,
      action: () => {
        chatStore.newConversation();
        showChat();
      },
    },
    {
      id: 'act-new-calendar',
      label: 'New calendar event',
      group: 'actions',
      icon: Plus,
      action: () => navigate('/calendar'),
    },

    // Conversations
    ...conversations.slice(0, PALETTE_MAX_CONVERSATIONS).map((c) => ({
      id: `conv-${c.id}`,
      label: c.title,
      group: 'conversations' as const,
      icon: Zap,
      action: () => {
        void chatStore.switchConversation(c.id);
        showChat();
      },
      keywords: c.provider,
    })),
  ];

  // Filter by query
  const filtered = query.trim()
    ? items.filter((item) => {
        const q = query.toLowerCase();
        return (
          item.label.toLowerCase().includes(q) || item.keywords?.toLowerCase().includes(q) || item.group.includes(q)
        );
      })
    : items;

  // Group filtered items
  const groups = [
    { key: 'navigation', label: 'Navigation', items: filtered.filter((i) => i.group === 'navigation') },
    { key: 'actions', label: 'Actions', items: filtered.filter((i) => i.group === 'actions') },
    { key: 'conversations', label: 'Recent Conversations', items: filtered.filter((i) => i.group === 'conversations') },
  ].filter((g) => g.items.length > 0);

  const flatItems = groups.flatMap((g) => g.items);

  // Keyboard shortcut to open
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpen((v) => !v);
        setQuery('');
        setSelectedIndex(0);
      }
      if (e.key === 'Escape') setOpen(false);
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Focus input when opened
  useEffect(() => {
    if (open) {
      inputRef.current?.focus();
      chatStore.loadConversations();
    }
  }, [open]);

  // Reset selection when filter changes
  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((i) => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flatItems[selectedIndex]) {
      e.preventDefault();
      flatItems[selectedIndex].action();
      setOpen(false);
    }
  }

  if (!open) return null;

  let itemIndex = -1;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]" onClick={() => setOpen(false)}>
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />

      {/* Palette */}
      <div
        data-testid="command-palette"
        className="relative w-full max-w-lg rounded-xl border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 border-b border-border px-4 py-3">
          <Search className="h-4 w-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            type="text"
            data-testid="command-palette-input"
            placeholder="Search pages, conversations, actions..."
            className="flex-1 bg-transparent text-sm text-foreground outline-none placeholder:text-muted-foreground/60"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd className="rounded border border-border px-1.5 py-0.5 text-xs text-muted-foreground">esc</kbd>
        </div>

        {/* Results */}
        <div className="max-h-80 overflow-y-auto p-2">
          {groups.length === 0 ? (
            <p className="py-6 text-center text-xs text-muted-foreground">No results</p>
          ) : (
            groups.map((group) => (
              <div key={group.key}>
                <p className="px-2 pb-1 pt-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {group.label}
                </p>
                {group.items.map((item) => {
                  itemIndex++;
                  const idx = itemIndex;
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      data-testid={`command-item-${item.id}`}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                        idx === selectedIndex
                          ? 'bg-accent text-accent-foreground'
                          : 'text-foreground hover:bg-muted/50',
                      )}
                      onClick={() => {
                        item.action();
                        setOpen(false);
                      }}
                      onMouseEnter={() => setSelectedIndex(idx)}
                    >
                      <Icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="truncate">{item.label}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center justify-between border-t border-border px-4 py-2">
          <span className="text-xs text-muted-foreground">
            <kbd className="rounded border border-border px-1 py-0.5 text-xs">↑↓</kbd> navigate{' '}
            <kbd className="rounded border border-border px-1 py-0.5 text-xs">↵</kbd> select
          </span>
          <span className="text-xs text-muted-foreground">
            <kbd className="rounded border border-border px-1 py-0.5 text-xs">⌘K</kbd> toggle
          </span>
        </div>
      </div>
    </div>
  );
}
